/**
 * 速報 API（/api/voyage-reports, /api/calc/*）。担当: G2
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { GearFindingDto, SightingDto } from './sightings';

export type SeaClass = 'S' | 'A' | 'B';

export interface VoyageListItem {
  id: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  vesselNameKana: string;
  tonnage: number;
  captainName: string;
  registrationNo: string;
  periodFrom: string;
  periodTo: string;
  actualWorkDays: number;
  catchSurveyStatus: string | null;
  noticeVesselId: number | null;
}

export interface VoyageListFilter {
  businessOperatorId?: number | '';
  vesselName?: string;
  registrationNo?: string;
  surveyFrom?: string;
  surveyTo?: string;
  page?: number;
}

export interface SurveyDayDto {
  id: number;
  surveyDate: string;
  surveyHours: number;
  zoneNumbers: number[];
  zoneClasses: string[];
  surveyClass: SeaClass | null;
  impression: string | null;
  wageExclTax: number;
  wageTax: number;
  wageTotal: number;
  charterExclTax: number;
  charterTax: number;
  charterTotal: number;
  taxRate: number;
  subsidyScheduledOn: string | null;
  subsidyPaidOn: string | null;
}

export interface ExpenseDto {
  fuelDate: string | null;
  fuelDaysAfterReturn: number | null;
  fuelType: string | null;
  fuelTaxRounding: string;
  fuelQuantity: number | null;
  fuelUnitPrice: number | null;
  fuelLightOilTax: number;
  fuelAmountExclTax: number;
  fuelTax: number;
  fuelTotal: number;
  fuelNote: string | null;
  lubeDate: string | null;
  lubeCoefficient: number | null;
  lubeUnitPrice: number | null;
  lubeAmountExclTax: number;
  lubeTotal: number;
  devDate: string | null;
  devTaxRounding: string;
  devAmountExclTax: number;
  devTax: number;
  devTotal: number;
}

export interface VoyageDetail {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  vesselNameKana: string;
  registrationNo: string;
  tonnage: number;
  noticeVesselId: number | null;
  periodFrom: string;
  periodTo: string;
  captainName: string;
  surveyor2Name: string | null;
  surveyor3Name: string | null;
  bearerCount: number;
  ownerName: string | null;
  isInvoiceTargetSurvey: boolean;
  catchSurveyStatus: string | null;
  crewCount: number;
  actualWorkDays: number;
  reportSubmittedOn: string | null;
  reporterName: string | null;
  note: string | null;
  surveyDays: SurveyDayDto[];
  expense: ExpenseDto | null;
  sightings: SightingDto[];
  gearFindings: GearFindingDto[];
  priorSeaAreaDays: { S: number; A: number; B: number };
  /** 助成金支払日が登録済み（調査期間は変更不可） */
  isSubsidyPaid?: boolean;
}

export interface VoyageNewContext {
  existingId: number | null;
  prefill: {
    noticeVesselId: number;
    fiscalYear: number;
    businessOperatorId: number;
    businessOperatorName: string;
    vesselId: number;
    vesselName: string;
    vesselNameKana: string;
    registrationNo: string;
    tonnage: number;
    taxRounding: string;
    approvalFrom: string | null;
    approvalTo: string | null;
    periodFrom: string;
    periodTo: string;
    captainName: string;
    captainOptions: string[];
    crewOptions: string[];
    ownerName: string;
    boardingCount: number | null;
  };
  surveyDays: { surveyDate: string; surveyHours: number; zoneNumbers: number[]; surveyClass: SeaClass | null; impression: string | null }[];
  priorSeaAreaDays: { S: number; A: number; B: number };
  sightings: SightingDto[];
  gearFindings: GearFindingDto[];
  expenseDefaults: { fuelType: string | null; fuelTaxRounding: string; devTaxRounding: string };
}

export interface PreviewIssue {
  message: string;
  rowIndex?: number;
  field?: string;
  path?: string;
  tab?: string;
}

export interface PreviewResult {
  errors: PreviewIssue[];
  warnings: PreviewIssue[];
  confirmations: { key: string; message: string }[];
  missingConfirmations: string[];
  calc: {
    crewCount: number;
    actualWorkDays: number;
    taxRate: number;
    hasConsumptionTax: boolean;
    priorSeaAreaDays: { S: number; A: number; B: number };
    seaAreaDays: { S: number; A: number; B: number; total: number };
    limits: { S: number; A: number; B: number; total: number };
    autoClasses: (SeaClass | null)[];
    surveyDays: (Omit<SurveyDayDto, 'id' | 'subsidyScheduledOn' | 'subsidyPaidOn'> & { rowClass: SeaClass | null })[];
    lube: { coefficient: number; amountExclTax: number; total: number };
    fuelDaysAfterReturn: number | null;
  };
}

export const voyageKeys = {
  all: ['voyage-reports'] as const,
  list: (year: number, f: VoyageListFilter) => [...voyageKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...voyageKeys.all, 'detail', id] as const,
  context: (rowId: number) => [...voyageKeys.all, 'context', rowId] as const,
};

export const PAGE_SIZE = 50;

export function useVoyages(year: number, f: VoyageListFilter) {
  return useQuery({
    queryKey: voyageKeys.list(year, f),
    queryFn: ({ signal }) => api.get<{ items: VoyageListItem[]; total: number }>('/voyage-reports', { fiscalYear: year, pageSize: PAGE_SIZE, ...f }, { signal }),
    placeholderData: keepPreviousData,
  });
}

export function useVoyage(id: number | null) {
  return useQuery({
    queryKey: voyageKeys.detail(id ?? 0),
    queryFn: ({ signal }) => api.get<VoyageDetail>(`/voyage-reports/${id}`, undefined, { signal }),
    enabled: id != null,
  });
}

export function useVoyageNewContext(rowId: number | null) {
  return useQuery({
    queryKey: voyageKeys.context(rowId ?? 0),
    queryFn: ({ signal }) => api.get<VoyageNewContext>('/voyage-reports/new-context', { noticeVesselId: rowId }, { signal }),
    enabled: rowId != null,
  });
}

export const previewVoyage = (input: unknown) => api.post<PreviewResult>('/voyage-reports/preview', input);

export function useSaveVoyage(id: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) =>
      id ? api.put<{ id: number; warnings: PreviewIssue[] }>(`/voyage-reports/${id}`, input) : api.post<{ id: number; warnings: PreviewIssue[] }>('/voyage-reports', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['advance-notices'] });
      // 新規作成用コンテキスト（existingId）は保存直後に再取得しない。再取得すると登録画面が
      // 「既存あり → 編集画面へ」リダイレクトし、保存後の詳細画面への遷移を上書きする（障害 BUG-004）。
      void qc.invalidateQueries({ queryKey: [...voyageKeys.all, 'context'], refetchType: 'none' });
      return qc.invalidateQueries({ queryKey: voyageKeys.all, predicate: (q) => q.queryKey[1] !== 'context' });
    },
  });
}

export function useDeleteVoyage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/voyage-reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: voyageKeys.all }),
  });
}

export const calcFuelTax = (input: Record<string, unknown>) =>
  api.post<{ amountExclTax: number; tax: number; lightOilTax: number; taxRate: number; lightOilTaxRate: number }>('/calc/fuel-tax', input);
export const calcLubricant = (input: Record<string, unknown>) => api.post<{ coefficient: number; amountExclTax: number; tax: number; total: number }>('/calc/lubricant', input);
export const calcDevelopment = (input: Record<string, unknown>) => api.post<{ amountExclTax: number; tax: number; taxRate: number }>('/calc/development', input);

export const CATCH_LABELS: Record<string, string> = {
  A: '漁獲調査を行い、漁獲物を販売した',
  B: '漁獲調査を行ったが、漁獲物は販売しなかった',
  C: '漁獲調査を行ったが、漁獲物が得られなかった',
  D: '漁獲調査を行わなかった',
};
